import * as DialogPrimitive from '@radix-ui/react-dialog';
import { ShieldCheck, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, ErrorState, Skeleton } from '@/components/ui';
import { formatDate, formatNumber } from '@/lib/utils';

import { useBusinessActivity } from './admin-api';
import type { RangeDays } from './admin-schemas';
import { BusinessActions } from './business-actions';
import { BusinessBilling } from './business-billing';
import { BarList, TimeBars } from './charts';
import { formatDay, langLabel, tradeLabel } from './labels';

/**
 * מגירת עסק: ציר פעילות יומי וספירות. אין כאן שום פרט של לקוח — השרת מחזיר
 * רק מספרים, שמות פעולות קבועים ותבניות מסלול.
 */
export function BusinessDrawer({
  businessId,
  days,
  onClose,
}: {
  businessId: string | null;
  days: RangeDays;
  onClose: () => void;
}) {
  const { t } = useTranslation(['admin', 'common']);
  const q = useBusinessActivity(businessId, days);
  const d = q.data;

  return (
    <DialogPrimitive.Root open={businessId !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content className="fixed inset-y-0 end-0 z-50 flex w-full max-w-xl flex-col border-s border-border bg-surface-raised shadow-lg data-[state=open]:animate-in data-[state=open]:fade-in-0">
          <div className="flex items-start gap-3 border-b border-border px-5 py-4">
            <div className="min-w-0 flex-1">
              <DialogPrimitive.Title className="truncate text-sm font-semibold text-fg">
                {d?.business.name ?? t('drawer.title')}
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="mt-0.5 flex items-center gap-1.5 text-xs text-fg-muted">
                <ShieldCheck className="size-3.5 text-success" aria-hidden />
                {t('drawer.privacy')}
              </DialogPrimitive.Description>
            </div>
            <DialogPrimitive.Close
              className="grid size-8 place-items-center rounded-(--radius-sm) text-fg-subtle transition-colors hover:bg-surface-hover hover:text-fg"
              aria-label={t('common:actions.close')}
            >
              <X className="size-4" aria-hidden />
            </DialogPrimitive.Close>
          </div>

          <div className="flex-1 space-y-6 overflow-y-auto p-5">
            {q.isError ? (
              <ErrorState error={q.error} onRetry={() => void q.refetch()} />
            ) : !d ? (
              <div className="space-y-3">
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-28 w-full" />
                <Skeleton className="h-40 w-full" />
              </div>
            ) : (
              <>
                <div className="flex flex-wrap gap-1.5">
                  <Badge tone="violet">{tradeLabel(d.business.trade)}</Badge>
                  <Badge tone="teal">{langLabel(d.business.locale)}</Badge>
                  <Badge
                    tone={
                      d.business.setupStage && d.business.setupStage !== 'DONE'
                        ? 'warning'
                        : 'success'
                    }
                  >
                    {t('drawer.setupStage')}:{' '}
                    {d.business.setupStage && d.business.setupStage !== 'DONE'
                      ? d.business.setupStage
                      : t('drawer.setupDone')}
                  </Badge>
                  {d.business.deletionScheduledFor ? (
                    <Badge tone="danger">{t('status.pendingDeletion')}</Badge>
                  ) : !d.business.isActive ? (
                    <Badge tone="danger">{t('businesses.inactive')}</Badge>
                  ) : null}
                </div>

                <BusinessActions business={d.business} />

                <BusinessBilling businessId={d.business.id} businessName={d.business.name} />

                <dl className="grid grid-cols-3 gap-3 sm:grid-cols-4">
                  <Stat label={t('drawer.created')}>{formatDate(d.business.createdAt)}</Stat>
                  <Stat label={t('drawer.users')}>{formatNumber(d.business.users)}</Stat>
                  <Stat label={t('drawer.customers')}>{formatNumber(d.business.customers)}</Stat>
                  <Stat label={t('drawer.tasks')}>{formatNumber(d.business.tasks)}</Stat>
                  <Stat label={t('drawer.quotes')}>{formatNumber(d.business.quotes)}</Stat>
                  <Stat label={t('drawer.contracts')}>{formatNumber(d.business.contracts)}</Stat>
                  <Stat label={t('drawer.integrations')}>
                    {formatNumber(d.business.integrations)}
                  </Stat>
                </dl>

                <section>
                  <h3 className="mb-2 text-xs font-semibold text-fg">{t('drawer.timeline')}</h3>
                  <Legend />
                  <div className="space-y-3">
                    <TimeBars
                      tone="accent"
                      label={t('drawer.activeUsers')}
                      points={d.timeline.map((p) => ({
                        key: p.day,
                        value: p.activeUsers,
                        title: formatDay(p.day),
                      }))}
                      describe={(p) =>
                        `${t('drawer.activeUsers')} · ${t('usage.dayValue', { date: formatDay(p.key), count: formatNumber(p.value) })}`
                      }
                    />
                    <TimeBars
                      tone="violet"
                      label={t('drawer.pageViews')}
                      points={d.timeline.map((p) => ({
                        key: p.day,
                        value: p.pageViews + p.clicks,
                        title: formatDay(p.day),
                      }))}
                      describe={(p) =>
                        `${t('drawer.pageViews')} · ${t('usage.dayValue', { date: formatDay(p.key), count: formatNumber(p.value) })}`
                      }
                    />
                    <TimeBars
                      tone="teal"
                      label={t('drawer.actions')}
                      points={d.timeline.map((p) => ({
                        key: p.day,
                        value: p.actions,
                        title: formatDay(p.day),
                      }))}
                      describe={(p) =>
                        `${t('drawer.actions')} · ${t('usage.dayValue', { date: formatDay(p.key), count: formatNumber(p.value) })}`
                      }
                    />
                  </div>
                </section>

                <section>
                  <h3 className="mb-2 text-xs font-semibold text-fg">{t('drawer.topActions')}</h3>
                  <BarList
                    tone="teal"
                    empty={t('drawer.none')}
                    items={d.topActions.map((a) => ({
                      key: a.action,
                      label: <Mono>{a.action}</Mono>,
                      value: a.count,
                    }))}
                  />
                </section>
                <section>
                  <h3 className="mb-2 text-xs font-semibold text-fg">{t('drawer.topRoutes')}</h3>
                  <BarList
                    empty={t('drawer.none')}
                    items={d.topRoutes.map((r) => ({
                      key: r.route,
                      label: <Mono>{r.route}</Mono>,
                      value: r.views,
                    }))}
                  />
                </section>
                <section>
                  <h3 className="mb-2 text-xs font-semibold text-fg">{t('drawer.topClicks')}</h3>
                  <BarList
                    tone="violet"
                    empty={t('drawer.none')}
                    items={d.topClicks.map((c) => ({
                      key: c.code,
                      label: <Mono>{c.code}</Mono>,
                      value: c.clicks,
                    }))}
                  />
                </section>
              </>
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function Stat({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="rounded-(--radius-md) border border-border bg-surface-sunken/50 px-3 py-2">
      <dt className="text-2xs text-fg-muted">{label}</dt>
      <dd className="tabular text-sm font-semibold text-fg">{children}</dd>
    </div>
  );
}

function Mono({ children }: { children: string }) {
  return (
    <span dir="ltr" className="font-mono text-2xs">
      {children}
    </span>
  );
}

function Legend() {
  const { t } = useTranslation('admin');
  const items = [
    { label: t('drawer.activeUsers'), cls: 'bg-accent' },
    { label: `${t('drawer.pageViews')} + ${t('drawer.clicks')}`, cls: 'bg-violet' },
    { label: t('drawer.actions'), cls: 'bg-teal' },
  ];
  return (
    <ul className="mb-2 flex flex-wrap gap-3 text-2xs text-fg-muted">
      {items.map((i) => (
        <li key={i.label} className="flex items-center gap-1.5">
          <span className={`size-2 rounded-sm ${i.cls}`} aria-hidden />
          {i.label}
        </li>
      ))}
    </ul>
  );
}
